import {
  ChangeEvent,
  ClipboardEvent,
  createRef,
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { TFunction } from 'i18next'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LoadingIcon, PinInput as DesignSystemPinInput } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { FieldController } from '../FieldController/FieldController'
import { ErrorText } from '../FieldErrorText'
import { FormInput } from '../FormInput'

const ANIMATION_DURATION = 300
const ICON_SIZE = 48

const shakeAnimation = keyframes`
  0% { transform: translateX(-10px) }
  16% { transform: translateX(9px) }
  33% { transform: translateX(-6px) }
  50% { transform: translateX(5px) }
  66% { transform: translateX(-2px) }
  83% { transform: translateX(1px) }
  100% { transform: translateX(0px) }
`

const StyledInput = styled.input<{ hasError: boolean }>`
  border-radius: 0.75rem;
  width: 3.75rem;
  height: 4rem;
  border-color: transparent;
  background-color: ${props => props.theme.colors.formFieldBackground};
  color: transparent;
  ${props => props.theme.typography.body};
  caret-color: transparent;

  :focus,
  :focus-visible {
    border: 2px solid ${props => props.theme.colors.outlineButtonSelectedBorder};
    background-color: ${props => props.theme.colors.background};
    outline: transparent;
  }

  ::selection {
    background: transparent;
  }

  ${props =>
    props.hasError &&
    css`
      animation: ${shakeAnimation} ${ANIMATION_DURATION}ms linear;
    `}
`

const Container = styled.div`
  display: flex;
  min-height: 4rem;
  padding: 0 ${({ theme }) => theme.spacing.xs};
`

// Matches Container's horizontal inset so the error lines up with the pin boxes. PaddingInset
// (the usual ErrorText wrapper) is gated on a different arcade-migration flag than this legacy
// path, so it can't be relied on to stay in step.
const ValidationErrorText = styled(ErrorText)`
  padding: 0 ${({ theme }) => theme.spacing.xs};
`

const Circle = styled.div`
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  position: absolute;
  background: ${props => props.theme.colors.label};
  inset: 0 0 0 0;
  margin: auto;
  z-index: 999;
`

const PinInputContainer = styled.div`
  position: relative;

  :not(:last-child) {
    margin-right: ${({ theme }) => theme.spacing.m};
  }
`

type PinInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  name: string
  pinLength: number
  onComplete?: (pin: string) => void
  /**
   * Validates a completed pin before it is accepted. Returning an error message rejects the pin:
   * the inputs shake, the entered digits are cleared for a fresh attempt, the message is shown
   * inline below the inputs, and `onComplete` is not called.
   */
  validatePin?: (pin: string) => string | undefined
}

type PinInputBoxProps = {
  index: number
  pin: Array<string>
  inputRefs: Array<RefObject<HTMLInputElement>>
  autoTab: (event: KeyboardEvent, index: number) => void
  handleChange: (event: ChangeEvent<HTMLInputElement>, index: number) => void
  handlePaste: (event: ClipboardEvent) => void
  handleOnClick: (index: number) => void
  hasError: boolean
  autoFocus: boolean
}

const isNumeric = (value: string) => {
  return /^\d+$/.test(value)
}

const getPinInputBoxAriaLabel = (index: number, t: TFunction) => {
  const digitNumber = index + 1
  const label = t(`Pin code digit {digitNumber}`, {
    digitNumber,
    context: 'accessible label for single digit input boxes making up a pin code',
  })
  const fisrtInputInstructionsWithLabel = t(
    `Enter pin code. After each digit is entered, the focus will automatically shift to the next input field.  When the code is completed, it gets automatically submitted. {label}`,
    {
      label,
      context: 'accessible label for instructions on how to fill out a series of inputs making up a pin code',
    }
  )
  return index === 0 ? fisrtInputInstructionsWithLabel : label
}

const PinInputBox = ({
  index,
  pin,
  inputRefs,
  autoTab,
  handleChange,
  handlePaste,
  handleOnClick,
  hasError,
  autoFocus,
}: PinInputBoxProps) => {
  const { t } = useTranslation()
  const label = getPinInputBoxAriaLabel(index, t)
  return (
    <PinInputContainer onClick={() => handleOnClick(index)}>
      {pin[index] !== '' && <Circle data-testid="pincode-circle" />}
      <StyledInput
        ref={inputRefs[index]}
        aria-label={label}
        data-testid={'pincode-input'}
        onChange={event => handleChange(event, index)}
        onPaste={event => handlePaste(event)}
        onKeyUp={event => autoTab(event, index)}
        hasError={hasError}
        autoFocus={autoFocus}
        value={pin[index]}
        inputMode="numeric"
        pattern="[0-9]*"
      />
    </PinInputContainer>
  )
}

export const PinInput = ({ name, pinLength, onComplete, validatePin }: PinInputProps) => {
  const { t } = useTranslation()
  const {
    watch,
    setValue,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useFormContext()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const code = watch(name) || ''
  const [errorAtIndex, setErrorAtIndex] = useState(-1)
  const [validationError, setValidationError] = useState<string>()

  const emptyArray = useMemo(() => new Array(pinLength).fill(''), [pinLength])
  const [inputRefs] = useState(() => Array.from({ length: pinLength }, () => createRef<HTMLInputElement>()))

  const [pin, setPin] = useState(emptyArray)
  const pinCode = pin.join('').length === 0 ? '' : pin.join('')

  const handleFieldEdit = useCallback(
    (value: string) => {
      if (value === '') {
        return
      }

      if (validationError || errors[name]) {
        setValidationError(undefined)
        clearErrors(name)
      }
    },
    [clearErrors, errors, name, validationError]
  )

  const updatePinCode = useCallback(
    (newPin: Array<string>) => {
      setPin(newPin)
      const newCode = newPin.join('')
      setValue(name, newCode)
      handleFieldEdit(newCode)
    },
    [handleFieldEdit, name, setValue]
  )

  // on error, we reset code value back to ''
  // When this happens, we want to reset the value of the pin
  useEffect(() => {
    if (code === '' && !pin.every(val => val === '')) {
      setPin(emptyArray)
    }
  }, [code, emptyArray, pin])

  const passFocusToInput = useCallback(
    (index: number) => {
      /**
       * Pass focus to pin input at given index
       */
      if (inputRefs[index]) {
        inputRefs[index].current?.focus()

        setTimeout(() => {
          inputRefs[index].current?.setSelectionRange(0, 999)
        }, 0)
      }
    },
    [inputRefs]
  )

  // Remove the error so we can trigger the animation again
  useEffect(() => {
    if (!isArcadeMigrationEnabled && errorAtIndex > -1) {
      setTimeout(() => {
        setErrorAtIndex(-1)
      }, ANIMATION_DURATION)
    }
  }, [errorAtIndex, isArcadeMigrationEnabled])

  // The submit-time validate rule must stay pure. Once RHF commits its error, mirror the message
  // locally before clearing the field so the rejection remains visible for the fresh attempt.
  const fieldError = errors[name]
  useEffect(() => {
    if (fieldError?.type !== 'validate' || typeof fieldError.message !== 'string') {
      return
    }

    setValidationError(fieldError.message)
    if (code !== '') {
      setValue(name, '')
    }
  }, [code, fieldError, name, setValue])

  // A rejection clears the entry for a fresh attempt, so focus must restart at the first box.
  // The timer-path rejections already do this themselves; this covers the submit-path
  // (validate rule) rejection, where focus would otherwise be stranded on the submit button or
  // the last box. The rule rejects while the submit is still in flight, when the inputs are
  // disabled (Arcade) or unmounted (legacy) and unfocusable — so wait for isSubmitting to
  // settle before focusing. Arcade renders its own inputs internally with no ref API, so reach
  // its first box by the stable per-digit name.
  useEffect(() => {
    if (!validationError || isSubmitting) {
      return
    }
    if (isArcadeMigrationEnabled) {
      document.querySelector<HTMLInputElement>(`input[name="${name}-0"]`)?.focus()
    } else {
      passFocusToInput(0)
    }
  }, [validationError, isSubmitting, isArcadeMigrationEnabled, name, passFocusToInput])

  // check to see if pin code is complete
  useEffect(() => {
    if (!isArcadeMigrationEnabled && pinCode.length === pinLength && !/\s/g.test(code)) {
      const timer = setTimeout(() => {
        const validationMessage = validatePin?.(pinCode)
        if (validationMessage) {
          setValidationError(validationMessage)
          setValue(name, '')
          setPin(emptyArray)
          passFocusToInput(0)
          return
        }
        setValue(name, pinCode)
        passFocusToInput(0)
        onComplete?.(pinCode)
        setPin(emptyArray)
      }, ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
    return
  }, [
    code,
    emptyArray,
    isArcadeMigrationEnabled,
    name,
    onComplete,
    passFocusToInput,
    pinCode,
    pinLength,
    setValue,
    validatePin,
  ])

  const autoTab = useCallback(
    (event: KeyboardEvent, index: number) => {
      /** Handle switching focus based on key press events.
       *
       * Backspace Key or Delete Key ->
       *     - If current focus has a value, delete that value and NOT move focus to Left
       *     - If current focus does not have a value, move focus to Left and delete value in that Left box
       * Left Arrow Key -> Focus to the Left
       * Right Arrow Key or same key value that's already in that index-> Focus to the Right
       * Control, Meta, `v` -> Do nothing (override for pasting)
       * Numeric key -> Update current index value to key pressed and shift focus to Right
       * Non numeric key -> Set error
       */
      let nextActiveInput = -1

      if (event.key === 'Backspace' || event.key === 'Delete') {
        const newPin = [...pin]
        if (newPin[index]) {
          newPin[index] = ''
        } else {
          newPin[index - 1] = ''
          nextActiveInput = index - 1
        }
        updatePinCode(newPin)
      } else if (event.key === 'ArrowLeft') {
        nextActiveInput = index - 1
        // when a user re-enters the same value as the existing value in an input,
        // we shift focus to the right because the onChange handler is not called
      } else if (event.key === 'ArrowRight' || pinCode[index] === event.key) {
        nextActiveInput = index + 1
      } else if (event.key === 'Tab' || event.key === 'Meta' || event.key === 'Control' || event.key === 'v') {
        //do nothing
      } else if (!isNumeric(event.key)) {
        setErrorAtIndex(index)
      }
      passFocusToInput(nextActiveInput)
    },
    [passFocusToInput, pin, updatePinCode, pinCode]
  )

  const handlePaste = (event: ClipboardEvent) => {
    /**
     * Handle a paste event inside a PinInput.
     * Conditions:
     * - paste content is not all numeric -> set an error
     * - paste content length < pinLength -> should fill in the entire pasted string
     * - paste content length > pinLength -> should fill in the first x digits of the pasted string where x is pinLength
     * - paste content length === pinLength -> fill the pin input and call onComplete
     *
     */
    event.preventDefault()
    const paste = event.clipboardData?.getData('text') ?? ''
    const pasteValue = paste.slice(0, pinLength)
    const focusIndex = Math.min(pinLength - 1, pasteValue.length)

    const isArrayNumeric = pasteValue.split('').every((value: string) => isNumeric(value))
    if (isArrayNumeric) {
      const newPin = emptyArray.map((_, index) => {
        const value = pasteValue[index]
        return isNumeric(value) ? value : ''
      })
      updatePinCode(newPin)
      passFocusToInput(focusIndex)
    } else {
      updatePinCode(emptyArray)
      setErrorAtIndex(0)
    }
  }

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number) => {
      /**
       * Handle when a value changes inside a PinInput
       *
       * If the value is numeric, update the pin with new value and move focus to the right.
       * Else, set an error at the given index.
       *
       * Note: If there is an existing value in the input, we override it with the new value
       */
      const value = event.currentTarget.value

      const newPin = [...pin]

      // if all the way to left input, we want to take the first char
      // else we want to take the last char
      const valueIndex = index === 0 ? 0 : value.length - 1
      newPin[index] = value[valueIndex]
      // validate that new input is numeric
      if (isNumeric(value)) {
        updatePinCode(newPin)
        passFocusToInput(index + 1)
      }
    },
    [pin, updatePinCode, passFocusToInput]
  )

  const handleValidatedComplete = (pin: string) => {
    const validationMessage = validatePin?.(pin)
    if (validationMessage) {
      setValidationError(validationMessage)
      setValue(name, '')
      return
    }
    onComplete?.(pin)
  }

  // The form value is complete as soon as the last digit is typed, but the completion callbacks
  // above only run validatePin behind the shake-animation timer. A pure form rule closes the gap
  // for a fast Continue click or Enter press; its committed error is handled by the effect above.
  const validatePinRules = validatePin
    ? {
        validate: (value: string) => {
          const validationMessage = validatePin(value)
          return validationMessage || true
        },
      }
    : undefined

  if (isArcadeMigrationEnabled) {
    return (
      <FieldController
        FieldComponent={DesignSystemPinInput}
        componentProps={{
          ariaLabel: t('PIN', {
            context: 'aria label for form field asking for a user PIN',
          }),
          disabled: isSubmitting,
          pinLength: pinLength,
          value: code,
          onComplete: validatePin ? handleValidatedComplete : onComplete,
          onChange: handleFieldEdit,
          isInvalid: !!validationError,
          errorText: validationError,
        }}
        name={name}
        defaultValue=""
        rules={{
          required: {
            value: true,
            message: t('Enter your pin.', {
              context: 'error message for form field with a missing PIN',
            }),
          },
          minLength: {
            value: pinLength,
            message: t('{pinLength} characters are expected.', {
              context: 'error message for a form field with too few characters in a PIN',
              pinLength,
            }),
          },
          maxLength: {
            value: pinLength,
            message: t('{pinLength} characters are expected.', {
              context: 'error message for a form field with too many characters in a PIN',
              pinLength,
            }),
          },
          ...validatePinRules,
        }}
      />
    )
  }

  return (
    <div>
      <Container>
        {isSubmitting ? (
          <LoadingIcon size={ICON_SIZE} />
        ) : (
          emptyArray.map((_, index) => {
            return (
              <PinInputBox
                key={index}
                pin={pin}
                index={index}
                inputRefs={inputRefs}
                autoTab={autoTab}
                handleChange={handleChange}
                handlePaste={handlePaste}
                hasError={errorAtIndex === index || !!validationError}
                autoFocus={index === 0}
                handleOnClick={passFocusToInput}
              />
            )
          })
        )}

        <FormInput
          key={name}
          type={'hidden'}
          showErrors={false}
          autoFocus={false}
          aria-label={t('PIN', {
            context: 'aria label for form field asking for a user PIN',
          })}
          name={name}
          rules={{
            required: {
              value: true,
              message: t('Enter your pin.', {
                context: 'error message for form field with a missing PIN',
              }),
            },
            minLength: {
              value: pinLength,
              message: t('{pinLength} characters are expected.', {
                context: 'error message for a form field with too few characters in a PIN',
                pinLength,
              }),
            },
            maxLength: {
              value: pinLength,
              message: t('{pinLength} characters are expected.', {
                context: 'error message for a form field with too many characters in a PIN',
                pinLength,
              }),
            },
            ...validatePinRules,
          }}
        />
      </Container>
      {validationError && <ValidationErrorText role="alert">{validationError}</ValidationErrorText>}
    </div>
  )
}
